import type { PathTileAtlasId } from '../pathArtStyles';
import { getPathTileArtStyle } from '../pathArtStyles';
import { resolveHexPathBrushMask } from './hexPathBrushCatalog';
import { composePathTileLayers, createCanvas, seededFloat } from './pathTileComposer';

interface Point {
  readonly x: number;
  readonly y: number;
}

const composedTileCache = new Map<string, HTMLCanvasElement>();
const atlasCache = new Map<string, HTMLCanvasElement>();
const CANONICAL_SIZE = 64;
const EDGE_DISTANCE = 17.32;
const PATH_WIDTH_OUTER = 20;
const PATH_WIDTH_INNER = 12;
const HUB_RADIUS_OUTER = 11.55; // 20 / sqrt(3)
const HUB_RADIUS_INNER = 6.93; // 12 / sqrt(3)
const STROKE_EDGE_DISTANCE = 30;
const WEB_CORNER_DISTANCE = 35;
const FILLET_DISTANCE_NARROW = 16;
const FILLET_DISTANCE_WIDE = 10;

function drawFlowerDecoration(
  context: CanvasRenderingContext2D,
  size: number,
  seed: string,
  count: number,
  bloomColors: readonly string[],
  centerColor: string,
  stemColor: string,
): void {
  for (let index = 0; index < count; index += 1) {
    const x = size * (0.22 + (seededFloat(`${seed}:x:${index}`) * 0.56));
    const y = size * (0.24 + (seededFloat(`${seed}:y:${index}`) * 0.48));
    const scale = 0.7 + (seededFloat(`${seed}:s:${index}`) * 0.45);
    const petalRadius = Math.max(1.2, size * 0.028 * scale);
    const bloomColor = bloomColors[index % bloomColors.length] ?? bloomColors[0] ?? '#fff';
    context.strokeStyle = stemColor;
    context.lineWidth = Math.max(1, size * 0.012 * scale);
    context.beginPath();
    context.moveTo(x, y + petalRadius * 2.8);
    context.lineTo(x, y + petalRadius);
    context.stroke();
    context.fillStyle = bloomColor;
    for (let petal = 0; petal < 4; petal += 1) {
      const angle = (petal * Math.PI) / 2;
      context.beginPath();
      context.arc(x + Math.cos(angle) * petalRadius * 1.2, y + Math.sin(angle) * petalRadius * 1.2, petalRadius, 0, Math.PI * 2);
      context.fill();
    }
    context.fillStyle = centerColor;
    context.beginPath();
    context.arc(x, y, petalRadius * 0.65, 0, Math.PI * 2);
    context.fill();
  }
}

function projectToIso(point: Point, center: Point, scale: number): Point {
  return {
    x: center.x + point.x * scale,
    y: center.y + point.y * scale,
  };
}

function activeDirections(mask: number): number[] {
  const directions: number[] = [];
  for (let bit = 0; bit < 6; bit += 1) {
    if ((mask & (1 << bit)) !== 0) {
      directions.push(bit);
    }
  }
  return directions;
}

function drawPolygon(context: CanvasRenderingContext2D, color: string, center: Point, scale: number, coords: Point[]): void {
  if (coords.length < 3) return;
  context.fillStyle = color;
  context.beginPath();
  for (let i = 0; i < coords.length; i += 1) {
    const pt = projectToIso(coords[i], center, scale);
    if (i === 0) context.moveTo(pt.x, pt.y);
    else context.lineTo(pt.x, pt.y);
  }
  context.closePath();
  context.fill();
}

function getHubPolygon(radius: number): Point[] {
  const points: Point[] = [];
  for (let i = 0; i < 6; i += 1) {
    const angle = i * (Math.PI / 3) + Math.PI / 6;
    points.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    });
  }
  return points;
}

export function composeHexPathTile(
  atlasId: PathTileAtlasId,
  size: number,
  mask: number,
  textureImage?: HTMLImageElement,
  textureTileScale?: number,
): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${size}:${mask}:${hasTexture ? 'tex' : 'notex'}:${textureTileScale ?? 'default'}`;
  const existing = composedTileCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const outerCanvas = createCanvas(size, size);
  const outerContext = outerCanvas.getContext('2d');
  if (!outerContext) {
    return outerCanvas;
  }
  const innerCanvas = createCanvas(size, size);
  const innerContext = innerCanvas.getContext('2d');
  if (!innerContext) {
    return outerCanvas;
  }

  outerContext.imageSmoothingEnabled = false;
  innerContext.imageSmoothingEnabled = false;
  outerContext.clearRect(0, 0, size, size);
  innerContext.clearRect(0, 0, size, size);

  const style = getPathTileArtStyle(atlasId);
  const center = { x: size / 2, y: size / 2 };
  const scale = size / CANONICAL_SIZE;

  if (mask === 0) {
    // Draw isolated patch
    if (style.borderColor) {
      drawPolygon(outerContext, style.borderColor, center, scale, getHubPolygon(HUB_RADIUS_OUTER));
      drawPolygon(innerContext, style.fillColor, center, scale, getHubPolygon(HUB_RADIUS_INNER));
    } else {
      drawPolygon(innerContext, style.fillColor, center, scale, getHubPolygon(HUB_RADIUS_OUTER));
    }
  } else {
    const active = activeDirections(mask);
    const drawSeamlessPathLayer = (
      target: CanvasRenderingContext2D,
      color: string,
      baseWidth: number,
      fillWebs: boolean,
    ) => {
      const pathWidth = baseWidth * scale;
      const spokeDistance = STROKE_EDGE_DISTANCE * scale;
      const cornerDistance = WEB_CORNER_DISTANCE * scale;

      target.beginPath();
      target.lineCap = 'round';
      target.lineJoin = 'round';
      target.lineWidth = pathWidth;
      target.strokeStyle = color;
      if (active.length === 0) return;

      for (const dir of active) {
        const angle = dir * (Math.PI / 3) - Math.PI / 2;
        const mX = spokeDistance * Math.cos(angle);
        const mY = spokeDistance * Math.sin(angle);

        target.moveTo(center.x, center.y);
        target.lineTo(center.x + mX, center.y + mY);
      }
      target.stroke();

      if (active.length > 1) {
        for (let i = 0; i < active.length; i++) {
          const d1 = active[i];
          const d2 = active[(i + 1) % active.length];
          let diff = d2 - d1;
          if (diff < 0) diff += 6;
          if (diff === 1 || diff === 2) {
            const angle1 = d1 * (Math.PI / 3) - Math.PI / 2;
            const angle2 = d2 * (Math.PI / 3) - Math.PI / 2;
            const filletDistance = (diff === 1 ? FILLET_DISTANCE_NARROW : FILLET_DISTANCE_WIDE) * scale;
            const p1x = center.x + filletDistance * Math.cos(angle1);
            const p1y = center.y + filletDistance * Math.sin(angle1);
            const p2x = center.x + filletDistance * Math.cos(angle2);
            const p2y = center.y + filletDistance * Math.sin(angle2);

            target.beginPath();
            target.moveTo(p1x, p1y);
            target.quadraticCurveTo(center.x, center.y, p2x, p2y);
            target.stroke();
          }
        }
      }

      // Draw standard inner circle to match the smooth center junction and round the exterior of the bends
      target.fillStyle = color;
      target.beginPath();
      target.arc(center.x, center.y, pathWidth / 2, 0, Math.PI * 2);
      target.fill();

      // If this layer should bridge adjacent active directions, fill the web between them completely!
      if (fillWebs && active.length > 1) {
        for (let i = 0; i < 6; i++) {
          const next = (i + 1) % 6;
          if ((mask & (1 << i)) && (mask & (1 << next))) {
            const angle1 = i * (Math.PI / 3) - Math.PI / 2;
            const angle2 = next * (Math.PI / 3) - Math.PI / 2;
            const cornerAngle = i * (Math.PI / 3) - Math.PI / 3;

            target.beginPath();
            target.moveTo(center.x, center.y);
            target.lineTo(center.x + spokeDistance * Math.cos(angle1), center.y + spokeDistance * Math.sin(angle1));
            target.lineTo(center.x + cornerDistance * Math.cos(cornerAngle), center.y + cornerDistance * Math.sin(cornerAngle));
            target.lineTo(center.x + spokeDistance * Math.cos(angle2), center.y + spokeDistance * Math.sin(angle2));
            target.fill();
          }
        }
      }
    };

    if (style.borderColor) {
      // Draw outer border (fills webs to maintain solid connections at junctions)
      drawSeamlessPathLayer(outerContext, style.borderColor, 20, true);
    }
    // Inner fill color (fills webs to create solid lakes or junctions)
    drawSeamlessPathLayer(
      innerContext,
      style.fillColor,
      style.borderColor ? 12 : 20,
      true
    );
  }

  const composed = composePathTileLayers({
    atlasId,
    tileSize: size,
    outerMask: style.borderColor ? outerCanvas : null,
    innerMask: innerCanvas,
    textureImage,
    textureTileScale,
    decorateInner: style.decoration?.kind === 'flowers'
      ? (target) => {
        const targetContext = target.getContext('2d');
        if (!targetContext) {
          return;
        }
        drawFlowerDecoration(
          targetContext,
          size,
          `${atlasId}:${size}:${mask}`,
          style.decoration.count,
          style.decoration.bloomColors,
          style.decoration.centerColor,
          style.decoration.stemColor,
        );
      }
      : null,
  });

  composedTileCache.set(cacheKey, composed);
  return composed;
}

export function composeHexPathBrushPreview(
  atlasId: PathTileAtlasId,
  size: number,
  tileIndex: number,
  textureImage?: HTMLImageElement,
  textureTileScale?: number,
): HTMLCanvasElement {
  const mask = resolveHexPathBrushMask(tileIndex) ?? 0;
  return composeHexPathTile(atlasId, size, mask, textureImage, textureTileScale);
}

export function composeHexPathAtlas(
  atlasId: PathTileAtlasId,
  tileSize: number,
  textureImage?: HTMLImageElement,
  textureTileScale?: number,
): HTMLCanvasElement {
  const hasTexture = textureImage && textureImage.complete;
  const cacheKey = `${atlasId}:${tileSize}:atlas:${hasTexture ? 'tex' : 'notex'}:${textureTileScale ?? 'default'}`;
  const existing = atlasCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  // Hex runtime rendering uses a full 64-mask procedural atlas.
  // The brush UI shows a smaller canonical subset, but the map renderer needs one tile per mask.
  const columns = 8;
  const rows = 8;
  const canvas = createCanvas(columns * tileSize, rows * tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }

  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < 64; index += 1) {
    const source = composeHexPathTile(atlasId, tileSize, index, textureImage, textureTileScale);
    const x = (index % columns) * tileSize;
    const y = Math.floor(index / columns) * tileSize;
    context.drawImage(source, x, y);
  }

  atlasCache.set(cacheKey, canvas);
  return canvas;
}
